import { useState } from "react";
import { toast } from "sonner";

import { useUploadKbFiles } from "@/shared/api/knowledge";
import { checkUploads, toBatches } from "../uploads";
import { plural } from "../format";

import type { KbUpload } from "@/shared/types";

/** A placeholder row: one per file, or one for a folder's worth of files. */
export interface PendingUpload {
  id: string;
  name: string;
  files: number;
  folder: boolean;
  kbId: string;
  parentId: string | null;
}

let seq = 0;

/** "specs/a.md" and "specs/b.md" are both the folder "specs" at the top. */
const topOf = (u: KbUpload) => (u.path ?? u.file.name).split("/")[0];

/** One row per top-level name. */
function placeholders(uploads: KbUpload[], kbId: string, parentId: string | null): PendingUpload[] {
  const byName = new Map<string, { files: number; folder: boolean }>();
  for (const u of uploads) {
    const top = byName.get(topOf(u)) ?? { files: 0, folder: false };
    byName.set(topOf(u), { files: top.files + 1, folder: top.folder || (u.path ?? "").includes("/") });
  }
  return [...byName].map(([name, row]) => ({ id: `upload-${seq++}`, name, ...row, kbId, parentId }));
}

/* Validates, then sends the batches one after another — a folder the first batch makes is
   there for the next to reuse rather than raced into a twin. A placeholder goes once the
   batch holding its last file lands (the refetched tree then has the real row); a folder
   still filling across batches is the table's to show (KbFolderView). */
export function useKbUpload() {
  const upload = useUploadKbFiles();
  const [pending, setPending] = useState<PendingUpload[]>([]);

  const start = (uploads: KbUpload[], kbId: string, parentId: string | null) => {
    if (!uploads.length) return;
    const problem = checkUploads(uploads);
    if (problem) return void toast.error(problem);

    const rows = placeholders(uploads, kbId, parentId);
    setPending((prev) => [...prev, ...rows]);
    const left = new Map(rows.map((r) => [r.name, r.files]));
    let sent = 0;
    const sendAll = async () => {
      for (const batch of toBatches(uploads)) {
        // one at a time on purpose (see above)
        // eslint-disable-next-line no-await-in-loop
        await upload.mutateAsync({ kbId, uploads: batch, parentId: parentId ?? undefined });
        sent += batch.length;
        for (const u of batch) left.set(topOf(u), (left.get(topOf(u)) ?? 1) - 1);
        const landed = new Set(rows.filter((r) => !left.get(r.name)).map((r) => r.id));
        setPending((prev) => prev.filter((p) => !landed.has(p.id)));
      }
    };
    // mutateAsync, not mutate's per-call callbacks: a second upload would detach those from the first.
    sendAll()
      .then(() => toast.success(`${plural(sent, "file")} uploaded`))
      .catch((e: Error) => toast.error(`${e.message} ${sent ? `${sent} of ${plural(uploads.length, "file")} were uploaded.` : "Nothing was uploaded."}`))
      .finally(() => setPending((prev) => prev.filter((p) => !rows.some((r) => r.id === p.id))));
  };

  return {
    start,
    pendingIn: (kbId: string, parentId: string | null) => pending.filter((p) => p.kbId === kbId && p.parentId === parentId),
  };
}
